import { tr } from '@/lib/i18n-core';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { DataTable, PageHeader } from '@/components/ui/layout';
import { Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export interface Rule {
  id: string;
  name: string;
  wardId: string | null;
  bedClass: string | null;
  chargeItemId: string;
  frequency: string;
  performerKind: string;
  isActive: boolean;
  ward: { id: string; name: string } | null;
  chargeItem: { id: string; code: string; name: string; basePriceMinor: number };
}

export function WardChargeRulesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const rules = useQuery({ queryKey: ['ward-charge-rules'], queryFn: () => get<Rule[]>('/ipd/ward-charge-rules') });

  const retire = useMutation({
    mutationFn: (id: string) => del(`/ipd/ward-charge-rules/${id}`),
    onSuccess: () => {
      toast.success(tr('ipd', 'rule_retired'), tr('ipd', 'charges_already_posted_stay_on_the_bills'));
      void queryClient.invalidateQueries({ queryKey: ['ward-charge-rules'] });
    },
    onError: (error) => toast.error(tr('ipd', 'could_not_retire_the_rule'), describeError(error)),
  });

  const manage = can('ward.charge.manage');

  return (
    <>
      <PageHeader
        title={tr('ipd', 'ward_charges')}
        description={tr('ipd', 'charges_the_system_adds_to_every_admitted')}
        actions={manage && <Button onClick={() => navigate('/ipd/charge-rules/new')}>{tr('ipd', 'add_a_rule')}</Button>}
      />

      <DataTable
        rows={rules.data}
        loading={rules.isLoading} refreshing={rules.isFetching}
        error={rules.error}
        rowKey={(row) => row.id}
        empty={tr('ipd', 'no_recurring_charges_yet_add_a_rule_for_the')}
        columns={[
          {
            header: tr('ipd', 'rule'),
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.chargeItem.code} · {row.chargeItem.name}
                </p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'applies_to'),
            cell: (row) =>
              row.ward || row.bedClass ? [row.ward?.name, row.bedClass ? `${humanize(row.bedClass)} beds` : null].filter(Boolean).join(' · ') : 'Every admitted patient',
          },
          { header: tr('ipd', 'how_often'), cell: () => 'Every day' },
          {
            header: tr('ipd', 'doctor'),
            sort: (row) => row.performerKind,
            cell: (row) =>
              row.performerKind === 'admitting_doctor'
                ? 'Admitting doctor'
                : '—',
          },
          { header: tr('ipd', 'price'), align: 'right', sort: (row) => row.chargeItem.basePriceMinor, cell: (row) => money(row.chargeItem.basePriceMinor) },
          { header: tr('ipd', 'status'), sort: (row) => row.isActive, cell: (row) => (row.isActive ? <Badge variant="success">{tr('ipd', 'active')}</Badge> : <Badge variant="muted">{tr('ipd', 'off')}</Badge>) },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              manage && (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/ipd/charge-rules/${row.id}/edit`)}>
                    {tr('ipd', 'edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => retire.mutate(row.id)}>
                    {tr('ipd', 'retire')}
                  </Button>
                </div>
              ),
          },
        ]}
      />
    </>
  );
}
